import MockApi from './mockApi';
import BaseApi from './baseApi';

export default class EmployeesApi extends BaseApi {
  static async findEmployees(query) {
    try {
      const result = await this.searchByQuery(`/employees/search`, query);
      return result.data;
    } catch (err) {
      throw err;
    }
  }

  static async getVaultEmployees(companyId) {
    if (MockApi.IsActive) {
      return { users: [], query: { companyId } };
    }
    const response = await this.axiosInstance.get(`${this.API}/employees/vault/${companyId}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  static async getPhonebookTemplate(companyId) {
    if (MockApi.IsActive) {
      return { headers: [] };
    }
    const response = await this.axiosInstance.get(`${this.API}/employees/phonebook-template/${companyId}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  static isDefaultQuery(query) {
    const defaultQuery = {
      page: 1,
      resultsPerPage: 10,
    };

    // Check if query has the same number of keys as defaultQuery
    if (Object.keys(query).length !== Object.keys(defaultQuery).length) {
      return false;
    }

    // Check if all keys and values in defaultQuery match the corresponding ones in query
    return Object.keys(defaultQuery).every((key) => query[key] === defaultQuery[key]);
  }

  static async searchByQuery(path, query, axiosConfig = {}) {
    if (MockApi.IsActive) {
      return MockApi.searchByQuery(path, query);
    }
    if (this.isDefaultQuery(query)) {
      // return this as async function
      return Promise.resolve({ data: { users: [], totalPages: 0 } });
    }

    const { headers = this.getAuthHeaders() } = axiosConfig;

    return this.axiosInstance.post(`${this.API}${path}`, query, {
      ...axiosConfig,
      headers,
    });
  }

  static getEmployeeDetails = (aCompanyId, aUserId) => {
    if (MockApi.IsActive) {
      return MockApi.GetEmployees();
    }
    if (aCompanyId === null) {
      return;
    }
    const config = {
      method: 'get',
      headers: this.getAuthHeaders(),
      params: {
        userId: aUserId,
      },
    };

    return this.axiosInstance.get(`${this.API}/employees/details?companyId=` + aCompanyId, config);
  };

  static addEmployee = (employee) => {
    let employeeJson = {};
    if (employee.employerId) {
      employeeJson['employerId'] = employee.employerId;
    }
    employeeJson['firstName'] = employee.firstName;
    employeeJson['lastName'] = employee.lastName;
    employeeJson['phoneNumber'] = employee.phoneNumber;
    employeeJson['role'] = employee.role;
    if (employee.organizationId) {
      employeeJson['organizationId'] = employee.organizationId;
    }
    employeeJson['details'] = {};
    employeeJson['details']['govId'] = employee.govId;

    if (employee.cityId) {
      employeeJson['details']['cityId'] = employee.cityId;
    }
    if (employee.dob) {
      employeeJson['details']['dob'] = employee.dob;
    }
    if (employee.gender) {
      employeeJson['details']['gender'] = employee.gender;
    }
    if (employee.internalId) {
      employeeJson['details']['internalId'] = employee.internalId;
    }
    if (employee.jobTitle) {
      employeeJson['details']['jobTitle'] = employee.jobTitle;
    }
    if (employee.jobDepartment) {
      employeeJson['details']['jobDepartment'] = employee.jobDepartment;
    }
    if (employee.branch) {
      employeeJson['details']['branch'] = employee.branch;
    }
    if (employee.mainProject) {
      employeeJson['details']['mainProject'] = employee.mainProject;
    }
    if (employee.metadata) {
      employeeJson['metadata'] = employee.metadata;
    }
    if (employee.bankId) {
      employeeJson['details']['bankAccountDetails'] = {};
      employeeJson['details']['bankAccountDetails']['bankId'] = employee.bankId;
      employeeJson['details']['bankAccountDetails']['accountId'] = employee.accountId;
      employeeJson['details']['bankAccountDetails']['branchId'] = employee.branchId;
    }
    if (employee.salaryPerHour) {
      employeeJson['details']['salaryDetails'] = {};
      employeeJson['details']['salaryDetails']['hourlyGrossRate'] = employee.salaryPerHour;
    } else if (employee.salaryPerMonth) {
      employeeJson['details']['salaryDetails'] = {};

      employeeJson['details']['salaryDetails']['monthlyGrossRate'] = employee.salaryPerMonth;
    }
    const body = JSON.stringify({
      data: [employeeJson],
    });

    const config = {
      method: 'post',
      headers: this.getAuthHeaders(),
    };

    return this.axiosInstance.post(`${this.API}/employees`, body, config);
  };

  static addEmployees = (employees) => {
    const body = JSON.stringify({
      data: employees,
    });

    const config = {
      method: 'post',
      headers: this.getAuthHeaders(),
    };

    return this.axiosInstance.post(`${this.API}/employees`, body, config);
  };

  static delete = (userId) => {
    const body = JSON.stringify({
      id: userId,
    });

    const config = {
      method: 'delete',
      headers: this.getAuthHeaders(),
      data: body,
    };

    return this.axiosInstance.delete(`${this.API}/employees`, config);
  };

  static update = (employeeJson) => {
    const body = JSON.stringify(employeeJson);

    const config = {
      method: 'post',
      headers: this.getAuthHeaders(),
      data: body,
    };

    return this.axiosInstance.post(`${this.API}/employees/update`, body, config);
  };

  static processLeavers = async ({ rows, targetStatus, reason }) => {
    const response = await this.axiosInstance.post(
      `${this.API}/employees/leavers-process`,
      { rows, targetStatus, reason },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  };

  static getNewUsers = (filters = {}) => {
    const config = {
      method: 'get',
      headers: this.getAuthHeaders(),
      params: filters,
    };

    return this.axiosInstance.get(`${this.API}/users/new`, config);
  };

  static uploadFile = (file, companyId) => {
    let formData = new FormData();
    formData.append('file', file);

    if (companyId) {
      formData.append('companyId', companyId);
    }

    const config = {
      headers: this.getAuthHeaders({ contentType: 'multipart/form-data' }),
    };

    return this.axiosInstance.post(`${this.API}/employees/upload/`, formData, config);
  };

  static testAlfonIntegration = (id, phoneNumber, companyId) => {
    const body = JSON.stringify({
      id: id,
      phoneNumber: phoneNumber,
      companyId: companyId,
    });

    const config = {
      method: 'post',
      headers: this.getAuthHeaders(),
    };

    return this.axiosInstance.post(`${this.API}/auth/test/`, body, config);
  };

  static debugShifts = (params) => {
    const config = { headers: this.getAuthHeaders() };
    return this.axiosInstance.post(`${this.API}/debug/shifts`, params, config);
  };

  static testEmployeesExcel = (formData) => {
    const config = {
      headers: this.getAuthHeaders({ contentType: 'multipart/form-data' }),
    };

    return this.axiosInstance.post(`${this.API}/auth/test/employees`, formData, config);
  };

  static updateUserAccess = (userId, data) => {
    const config = {
      method: 'put',
      headers: this.getAuthHeaders(),
    };

    return this.axiosInstance.put(`${this.API}/user/${userId}/access`, { data }, config);
  };
}
